import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Gallery = () => {
  const { t, language } = useLanguage();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGalleryItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchGalleryItems = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setItems(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching gallery items:', error);
      setLoading(false);
      // Use sample data if API fails
      setSampleData();
    }
  };

  const setSampleData = () => {
    const sampleItems = [
      {
        id: '1',
        title_fr: 'Huile d\'Olive Extra Vierge AL HAROUN',
        title_en: 'AL HAROUN Extra Virgin Olive Oil',
        description_fr: 'Huile d\'olive premium 100% originale de Tunisie',
        description_en: '100% original premium olive oil from Tunisia',
        category: 'huile',
        image_url: 'https://customer-assets.emergentagent.com/job_site-generator-110/artifacts/5kqww6he_IMG-20260221-WA0006.jpg'
      },
      {
        id: '2',
        title_fr: 'Legato d\'oro - Huile Premium',
        title_en: 'Legato d\'oro - Premium Oil',
        description_fr: 'Notre marque prestige pour l\'export international',
        description_en: 'Our prestige brand for international export',
        category: 'huile',
        image_url: 'https://customer-assets.emergentagent.com/job_site-generator-110/artifacts/5kqww6he_IMG-20260221-WA0006.jpg'
      },
      {
        id: '3',
        title_fr: 'Mosaïques Décoratives Sodever',
        title_en: 'Sodever Decorative Mosaics',
        description_fr: 'Mosaïques en verre pour architecture et design',
        description_en: 'Glass mosaics for architecture and design',
        category: 'mosaique',
        image_url: 'https://customer-assets.emergentagent.com/job_site-generator-110/artifacts/lic9eacp_IMG-20260221-WA0004.jpg'
      }
    ];
    setItems(sampleItems);
  };

  const filteredItems = filter === 'all' 
    ? items 
    : items.filter(item => item.category === filter);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-700 to-amber-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="gallery-title">{t.gallery.title}</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Filter Buttons */}
        <div className="flex justify-center space-x-4 mb-12">
          <button
            onClick={() => setFilter('all')}
            className={`px-6 py-2 rounded-full font-semibold transition-colors ${
              filter === 'all' 
                ? 'bg-amber-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-amber-50'
            }`}
            data-testid="filter-all"
          >
            {t.gallery.all}
          </button>
          <button
            onClick={() => setFilter('huile')}
            className={`px-6 py-2 rounded-full font-semibold transition-colors ${
              filter === 'huile' 
                ? 'bg-amber-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-amber-50'
            }`}
            data-testid="filter-oil"
          >
            {t.gallery.oil}
          </button>
          <button
            onClick={() => setFilter('mosaique')}
            className={`px-6 py-2 rounded-full font-semibold transition-colors ${
              filter === 'mosaique' 
                ? 'bg-amber-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-amber-50'
            }`}
            data-testid="filter-mosaic"
          >
            {t.gallery.mosaic}
          </button>
        </div>

        {/* Gallery Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            {language === 'fr' ? 'Aucun produit disponible' : 'No products available'}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="gallery-grid">
            {filteredItems.map((item) => (
              <div 
                key={item.id} 
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                data-testid={`gallery-item-${item.id}`}
              >
                <div 
                  className="h-64 bg-cover bg-center" 
                  style={{ backgroundImage: `url(${item.image_url})` }}
                ></div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-stone-800 mb-2">
                    {language === 'fr' ? item.title_fr : item.title_en}
                  </h3>
                  <p className="text-gray-600">
                    {language === 'fr' ? item.description_fr : item.description_en}
                  </p>
                  <div className="mt-4">
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                      item.category === 'huile' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-purple-100 text-purple-700'
                    }`}>
                      {item.category === 'huile' ? t.gallery.oil : t.gallery.mosaic}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Gallery;